import React, { useState, useMemo, useEffect } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { User } from "common/models/types";
import UserTable from "./UserTable";
import Button from "components/atoms/Button";
import Text from "components/atoms/Text";
import Input from "components/atoms/Input";
import { Flex } from "components/atoms";

interface UsersTemplateProps {
  users: User[];
  selectedUser: User | null;
  onSelectUser: (user: User) => void;
  onUpdateUser?: (user: User) => void;
  onSelectUserChange?: (user: User | null) => void;
  onReloadUser?: (userId: string) => void;
  onCreateUser?: () => void;
  onCreateBulkUser?: () => void;
  onSearch?: (role?: string) => void;
  // 무한 스크롤 관련 props
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  // 검색 시 남은 전체를 한 번에 로드
  onLoadAll?: () => void;
  isLoadingDetail?: boolean;
  onDeleteUser?: (userId: string) => void;
  onResetPassword?: (user: User) => void;
}

// 페이지 컨테이너 스타일
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100vh - 100px);
  padding: 24px;
  gap: 16px;
`;

// 헤더 영역 스타일
const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

// 필터 영역 스타일
const FilterSection = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

// 액션 버튼 영역
const ActionSection = styled.div`
  display: flex;
  gap: 8px;
`;

// 테이블 컨테이너 스타일
const TableSection = styled.div`
  flex: 1;
  overflow-y: auto;
`;

// 검색 입력 컨테이너
const SearchInputContainer = styled.div`
  width: 300px;
`;

const UsersTemplate: React.FC<UsersTemplateProps> = ({
  users,
  selectedUser,
  onSelectUser,
  onUpdateUser,
  onSelectUserChange,
  onReloadUser,
  onCreateUser,
  onCreateBulkUser,
  onSearch,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onLoadAll,
  isLoadingDetail,
  onDeleteUser,
  onResetPassword,
}) => {
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const handleSelectUser = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedUsers(users.map(user => user.id).filter(Boolean));
    } else {
      setSelectedUsers([]);
    }
  };

  // 클라이언트 사이드 검색 필터링
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) {
      return users;
    }
    
    const query = searchQuery.toLowerCase().trim();
    return users.filter(user => {
      return (
        user.id?.toLowerCase().includes(query) ||
        user.name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        user.company?.toLowerCase().includes(query) ||
        user.phone?.toLowerCase().includes(query) ||
        user.license?.toLowerCase().includes(query)
      );
    });
  }, [users, searchQuery]);
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // 검색 중에는 클라이언트 사이드 필터링이 전체 데이터를 필요로 하므로,
  // 아직 더 불러올 데이터가 남아 있으면 한 번의 요청으로 전체를 로드한다.
  // (검색어가 현재 로드된 목록에 없을 때 빈 리스트로 멈춰버리던 버그 방지)
  useEffect(() => {
    if (searchQuery.trim() && hasMore && !isLoadingMore) {
      // onLoadAll이 있으면 1회 대량 로드, 없으면 페이지 단위 폴백
      (onLoadAll || onLoadMore)?.();
    }
  }, [searchQuery, hasMore, isLoadingMore, onLoadAll, onLoadMore]);

  const handleViewUser = (user: User) => {
    onSelectUser(user);
  };

  return (
    <Container>
      <HeaderSection>
        <FilterSection>
          <Text textType="H3">사용자 관리</Text>
          <SearchInputContainer>
            <Input
              type="text"
              placeholder="ID, 이름, 이메일, 회사, 전화번호, 면허번호로 검색"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </SearchInputContainer>
        </FilterSection>
        
        <ActionSection>
          <Button
            buttonType="button"
            color="Primary"
            size="Medium"
            type="Text"
            variant="Contained"
            icon="Plus Circle"
            onClick={onCreateUser}
          >
            새 사용자
          </Button>
          <Button
            buttonType="button"
            color="Secondary"
            size="Medium"
            type="Text"
            variant="Contained"
            icon="Document Plus"
            onClick={onCreateBulkUser}
          >
            대량 생성
          </Button>
        </ActionSection>
      </HeaderSection>

      <TableSection>
        <UserTable
          users={filteredUsers}
          selectedUsers={selectedUsers}
          onSelectUser={handleSelectUser}
          onSelectAll={handleSelectAll}
          onViewUser={handleViewUser}
          hasMore={hasMore || false}
          isLoadingMore={isLoadingMore || false}
          onLoadMore={onLoadMore || (() => {})}
          isSearching={!!searchQuery.trim()}
        />
      </TableSection>
    </Container>
  );
};

export default UsersTemplate;
